import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { type Task, TaskScheduler } from '@air/classes-task-scheduler';
import {
  isFinishedUploadingSelector,
  nextUploadIdSelector,
  setAllFilesUploaded,
  type Upload,
  uploadByIdSelector,
  useUploaderStore,
} from '@air/redux-uploader';

import { MAX_UPLOAD_CONCURRENCY } from './getUploadConcurrencyForSpeed';
import { type UploadConcurrencyChangeParams } from './uploadConcurrencyTypes';
import { useAdaptiveUploadConcurrency } from './useAdaptiveUploadConcurrency';

export type UseUploadManagerProps = {
  adaptiveConcurrencyEnabled?: boolean;
  getUploadTasks: (params: { upload: Upload }) => Task[];
  onConcurrencyChange?: (params: UploadConcurrencyChangeParams) => void;
};

/**
 * This hook starts uploads if there is any new and sends 'isFinished' event when everything is uploaded
 */
export const useUploadManager = ({
  adaptiveConcurrencyEnabled = false,
  getUploadTasks,
  onConcurrencyChange,
}: UseUploadManagerProps) => {
  const store = useUploaderStore();
  const dispatch = useDispatch();
  const nextUploadId = useSelector(nextUploadIdSelector);
  const isFinished = useSelector(isFinishedUploadingSelector);

  const getUploadTasksRef = useRef(getUploadTasks);
  getUploadTasksRef.current = getUploadTasks;

  const SchedulerRef = useRef<TaskScheduler | undefined>();

  if (!SchedulerRef.current) {
    // Always start at MAX: the adaptive hook will reduce to 1 if/when a stall is detected.
    SchedulerRef.current = new TaskScheduler(MAX_UPLOAD_CONCURRENCY);
  }

  useEffect(() => {
    if (nextUploadId) {
      const nextUpload = uploadByIdSelector(store.getState(), nextUploadId);

      if (nextUpload) {
        const tasks = getUploadTasksRef.current({ upload: nextUpload });
        SchedulerRef.current?.addTasks(tasks);
      }
    }
  }, [nextUploadId, store]);

  useEffect(() => {
    if (isFinished) {
      SchedulerRef.current?.reset();
      dispatch(setAllFilesUploaded());
    }
  }, [dispatch, isFinished]);

  useAdaptiveUploadConcurrency({ adaptiveConcurrencyEnabled, onConcurrencyChange, schedulerRef: SchedulerRef });
};
